/**
 * Identification des appels — Phase 1 CardDAV (bloc A 11/10).
 *
 * Mobile-first 390px. Détecte iPhone via UA :
 *  - iPhone → bouton "Installer sur mon iPhone" (ouvre .mobileconfig directement)
 *  - Desktop/iPad/Android → QR code à scanner avec l'iPhone + saisie manuelle
 *
 * Mot de passe d'application affiché UNE SEULE FOIS (après clic Activer).
 */
import { useState, useEffect, useMemo } from "react";
import { Smartphone, ShieldCheck, Copy, EyeOff, RefreshCw, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, API_ROOT, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

function isIPhone() {
  const ua = navigator.userAgent || "";
  return /iPhone|iPod/.test(ua) && !/iPad/.test(ua);
}

const GUIDE_STEPS = [
  "Ouvrez cette page sur votre iPhone, ou scannez le QR code depuis votre ordinateur.",
  "Touchez le bouton « Installer sur mon iPhone ». iOS télécharge le profil.",
  "Allez dans Réglages (en haut, un bandeau « Profil téléchargé » apparaît).",
  "Touchez « Installer ». Un avertissement « Profil non vérifié » s'affiche — c'est normal, confirmez.",
  "Dans Réglages → Contacts → Comptes, vérifiez que « Wérko – <votre société> » apparaît.",
  "Les nouveaux contacts arrivent automatiquement (~15 min). Pour un ajout immédiat : app Contacts → tirez la liste vers le bas.",
];

export default function CallIdentificationPage() {
  const [status, setStatus] = useState({ active: false, loading: true });
  const [credentials, setCredentials] = useState(null); // {password, install_url, expires_in, token}
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);  // BLOC 11/10 14h10 — fermé par défaut
  const [qrSrc, setQrSrc] = useState(null);
  const [relayConfigured, setRelayConfigured] = useState(null); // null = chargement
  const onIPhone = useMemo(isIPhone, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/account/carddav/status");
      setStatus({ ...data, loading: false });
    } catch (e) {
      setStatus({ active: false, loading: false });
    }
  };

  const loadRelayConfig = async () => {
    try {
      const { data } = await api.get("/account/carddav/relay-config");
      setRelayConfigured(!!data?.relay_configured);
    } catch (e) {
      setRelayConfigured(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadRelayConfig();
  }, []);

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/carddav/activate");
      const token = data.install_url.split("/").pop().replace(".mobileconfig", "");
      setCredentials({ ...data, token });
      await loadStatus();
      // Charger QR code pour affichage desktop
      if (!onIPhone) {
        const base = API_ROOT.replace(/\/api$/, "");
        const qr = await fetch(`${base}/api/account/carddav/qrcode/${token}`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("lldls_token") || ""}` },
        });
        if (qr.ok) {
          const blob = await qr.blob();
          setQrSrc(URL.createObjectURL(blob));
        }
      }
      toast.success("Compte CardDAV activé. Notez le mot de passe maintenant — il ne sera plus affiché.");
    } catch (e) {
      toast.error("Activation impossible : " + formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Révoquer le compte CardDAV ? Votre iPhone ne pourra plus synchroniser les contacts Wérko.")) return;
    setBusy(true);
    try {
      await api.post("/account/carddav/revoke");
      setCredentials(null);
      setQrSrc(null);
      await loadStatus();
      toast.success("Compte CardDAV révoqué.");
    } catch (e) {
      toast.error("Révocation impossible : " + formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  };

  const base = API_ROOT.replace(/\/api$/, "");
  const installUrl = credentials ? `${base}${credentials.install_url}` : null;

  return (
    <div className="mx-auto max-w-xl p-4 space-y-4" data-testid="carddav-page">
      <div className="flex items-center gap-3">
        <Smartphone className="h-6 w-6 text-sky-400" />
        <h1 className="text-2xl font-semibold text-slate-100">Identification des appels</h1>
      </div>
      <p className="text-sm text-slate-400">
        Afficher le nom de vos clients et prospects Wérko quand ils vous appellent sur votre iPhone.
        Lecture seule, aucune donnée synchronisée vers Wérko depuis le téléphone.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            Statut
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {status.loading ? (
            <div className="text-sm text-slate-400">Chargement…</div>
          ) : status.active ? (
            <>
              {/* BLOC 12/10 18h05 — WARN 1 fix : bandeau neutre si relais down ET compte déjà actif */}
              {relayConfigured === false && (
                <Alert data-testid="carddav-relay-down-banner">
                  <AlertDescription>
                    L'identification des appels est momentanément indisponible.
                    Vos contacts déjà synchronisés restent sur votre iPhone.
                  </AlertDescription>
                </Alert>
              )}
              <div className="text-sm text-emerald-400" data-testid="carddav-status-active">
                ✓ Compte CardDAV actif
                {status.last_used_at && (
                  <span className="ml-2 text-slate-500">
                    (dernière sync : {new Date(status.last_used_at).toLocaleString("fr-BE")})
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={activate}
                  disabled={busy}
                  data-testid="carddav-regenerate-btn"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> Régénérer un mot de passe
                </Button>
                <Button variant="destructive" size="sm" onClick={revoke} disabled={busy} data-testid="carddav-revoke-btn">
                  Désactiver / révoquer
                </Button>
              </div>
            </>
          ) : (
            <>
              {relayConfigured === false ? (
                <>
                  <div className="text-sm text-amber-300" data-testid="carddav-not-ready">
                    Identification des appels bientôt disponible.
                  </div>
                  <Button
                    disabled
                    data-testid="carddav-activate-btn"
                    className="min-h-11 opacity-60 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    Activer sur cet iPhone
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-sm text-slate-400">Non activé.</div>
                  <Button
                    onClick={activate}
                    disabled={busy || relayConfigured === null}
                    data-testid="carddav-activate-btn"
                    className="min-h-11"
                  >
                    {onIPhone ? "Activer sur cet iPhone" : "Activer et obtenir le lien iPhone"}
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {credentials && (
        <Card className="border-amber-500/50 bg-amber-950/20">
          <CardHeader>
            <CardTitle className="text-base text-amber-300">
              Prêt à installer sur votre iPhone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* BLOC 11/10 14h10 — Fix 3 : QR en HAUT pour iPad/desktop (au-dessus du pli 844px) */}
            {onIPhone ? (
              <Button
                asChild
                size="lg"
                className="w-full min-h-14 bg-sky-500 hover:bg-sky-400 text-white"
                data-testid="carddav-install-iphone-btn"
              >
                <a href={installUrl}>Installer sur cet iPhone</a>
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="text-sm font-semibold text-slate-200 text-center">
                  Scannez avec l'appareil photo de votre iPhone
                </div>
                {qrSrc ? (
                  <img
                    src={qrSrc}
                    alt="QR code installation iPhone"
                    className="mx-auto h-56 w-56 rounded bg-white p-2"
                    data-testid="carddav-qrcode-img"
                  />
                ) : (
                  <div className="text-slate-500 flex items-center justify-center gap-2 h-56">
                    <QrCode className="h-4 w-4" />Chargement du QR code…
                  </div>
                )}
              </div>
            )}

            {/* BLOC 11/10 14h10 — Fix 3 : mdp sous lien discret, 99% des users n'y touchent pas */}
            <details className="text-sm text-slate-400 rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-testid="carddav-manual-config-details">
              <summary className="cursor-pointer text-slate-300 min-h-11 flex items-center">
                Configuration manuelle (si vous ne pouvez pas installer le profil)
              </summary>
              <div className="mt-3 space-y-2 font-mono text-xs">
                <div>Serveur : <span className="text-slate-100">{window.location.host}</span></div>
                <div>Identifiant : <span className="text-slate-100">votre email Wérko</span></div>
                <div>
                  Mot de passe :
                  <span className="ml-1 text-emerald-300" data-testid="carddav-password-display">
                    {showPwd ? credentials.password : "•".repeat(credentials.password.length)}
                  </span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(credentials.password, "Mot de passe")}
                    data-testid="carddav-copy-password-btn"
                    className="min-h-11"
                  >
                    <Copy className="mr-2 h-4 w-4" /> Copier
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowPwd(!showPwd)} className="min-h-11">
                    <EyeOff className="mr-2 h-4 w-4" /> {showPwd ? "Masquer" : "Afficher"}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 pt-2">
                  Le mot de passe n'est affiché qu'une seule fois. Si vous le perdez, cliquez sur « Régénérer un mot de passe ».
                </p>
              </div>
            </details>

            <p className="text-xs text-amber-200/80">
              ⚠️ iOS affichera un avertissement « Profil non vérifié ». C'est normal — la signature officielle du profil arrivera en phase 2.
            </p>
            <p className="text-xs text-slate-400">
              Les nouveaux contacts arrivent automatiquement (environ toutes les 15 minutes).
              Pour un ajout immédiat : ouvrez l'app Contacts et tirez la liste vers le bas.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Guide d'installation iPhone</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-300">
            {GUIDE_STEPS.map((step, i) => (
              <li key={i} data-testid={`carddav-guide-step-${i + 1}`}>{step}</li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-slate-500">
            Conseil : gardez votre compte iCloud comme compte Contacts par défaut.
            Les contacts Wérko s'affichent en plus, sans écraser les vôtres.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
